import { Formik } from 'formik';
import * as yup from 'yup';
import React from 'react';
import { StatusCodes } from 'http-status-codes';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';
import styled from 'styled-components';
import FileInputComponent from '../../../../components/common/forms/FileInput/FileInputComponent';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import { UPLOAD_MAX_SIZE_IMAGE, FILE_UPLOAD_TYPES } from '../../../../utils/constants';
import FilesApi from '../../../../services/api/FilesApi';
import ProductImportsApi from '../../../../services/api/ProductImportsApi';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import SecondaryButton from '~/src/components/common/forms/Button/SecondaryButton';
import { Colors } from '~/src/scss/common/style';
import uploadIcon from '~/src/static/img/upload.svg';
import exportIcon from '~/src/static/img/export.svg';
import DataOperationsNav from '~/src/components/common/navigation/DataOperationsNav';

const validationSchema = yup.object().shape({
  certificate: yup.mixed().test('fileSize', 'Моля използвайте файлове до 3MB', (value) => {

    if (typeof value === 'undefined' || value.length <= 0) {
      return true;
    }

    return value[0].size <= UPLOAD_MAX_SIZE_IMAGE;
  }),
});

export default function UploadForImportSupplierData(): JSX.Element {
  const [status, setStatus] = React.useState('');
  const [isExporting, setIsExporting] = React.useState(false);
  const history = useHistory();

  const onSubmitMine = async (values, setSubmitting) => {
    setSubmitting(true);
    const files = values.xlsx;

    if (files && files.length > 0) {
      const fileData = { files, type: FILE_UPLOAD_TYPES.PRODUCT_IMPORTS };
      var fileUploadResp = await FilesApi.create(fileData);

      if (fileUploadResp.status === StatusCodes.OK) {
        var res = await ProductImportsApi.importAnUploadSupplierData(fileUploadResp.data[0].id);
        setStatus(res.data);
      }
    }

    setSubmitting(false);
  };

  const onExportSupplierData = async () => {
    setIsExporting(true);
    const x = await ProductImportsApi.exportSupplierData();
    setIsExporting(false);
    const courseSheet = x.data;

    if (courseSheet) {
      const blob = new Blob([courseSheet], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `export-supplier-data.xlsx`);
    }
  };

  return (
    <WhitePad>
      <div style={{ display: 'flex' }}>
        <DataOperationsNav />
        <div>
          <TitleComponent title="Import Update Supplier Data" 
          titleLeft="Back"
          onLeftHandler={(): void => history.goBack()} 
          />
          <FormWrapper>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center;' }}>
              <Title>Export SUPPLIER DATA</Title>
              <ActionWrapper>
                <SecondaryButton
                  icon={exportIcon}
                  onClick={onExportSupplierData}
                  title="Export"
                  isSubmitting={isExporting}
                />
              </ActionWrapper>
            </div>
          </FormWrapper>
          <FormWrapper>
            <div>
              <Title>Instructions</Title>
              <StyleP>Upload an excel with the following columns in this specific order:</StyleP>
              <Table>
                <tbody>
                  <tr>
                    <StyleTd>
                      <b>CuGtin</b>
                    </StyleTd>
                    <StyleTd>
                      The Barcode of the individual unit.
                      <i>Used only to find the product, will not update if changed.</i>
                    </StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DuGtin</b>
                    </StyleTd>
                    <StyleTd>
                      The Barcode of the Distribution unit.
                      <i>Used only to find the product, will not update if changed.</i>
                    </StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DpGtin</b>
                    </StyleTd>
                    <StyleTd>
                      The Barcode of the display.
                      <i>Used only to find the product, will not update if changed.</i>
                    </StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>Name</b>
                    </StyleTd>
                    <StyleTd>
                      Name of the product.
                      <i>Used only to find the product, will not update if changed.</i>
                    </StyleTd>
                  </tr>
                </tbody>
              </Table>
              <br />
              <Table>
                <tbody>
                  <tr>
                    <StyleTd>
                      <b>SupplierName</b>
                    </StyleTd>
                    <StyleTd>Name of the Supplier (has to be exact as in the DB)</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>SKU</b>
                    </StyleTd>
                    <StyleTd>Supplier Barcode or SKU</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>ExternalName</b>
                    </StyleTd>
                    <StyleTd>Name given by supplier</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>PriceOfCuForDus</b>
                    </StyleTd>
                    <StyleTd>Supplier's price for a single unit when ordering small amount of boxes</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>PriceOfCuForLayers</b>
                    </StyleTd>
                    <StyleTd>Supplier's price for a single unit when ordering at least layer</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>PriceOfCuForPallets</b>
                    </StyleTd>
                    <StyleTd>Supplier's price for a single unit when ordering at least pallet</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>PriceOfDuForDus</b>
                    </StyleTd>
                    <StyleTd>Supplier's price for a box when ordering small amount of boxes</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>PriceOfDuForLayers</b>
                    </StyleTd>
                    <StyleTd>Supplier's price for a box when ordering at least layer</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>PriceOfDuForPallets</b>
                    </StyleTd>
                    <StyleTd>Supplier's price for a box when ordering at least pallet</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DefaultTemperatureControl</b>
                    </StyleTd>
                    <StyleTd>(yes/no) - Put "yes" if it requires Reefer</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>MOQPallet</b> (yes/no)
                    </StyleTd>
                    <StyleTd>Minimum Order Quantity is Pallet</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>MOQLayer</b>
                    </StyleTd>
                    <StyleTd>(yes/no) Minimum Order Quantity is Layer</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>HScodeSupplier</b>
                    </StyleTd>
                    <StyleTd>HS code by supplier</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>DutyEUfromUSA</b>
                    </StyleTd>
                    <StyleTd>Duty tax string from USA</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>MeursingCode</b>
                    </StyleTd>
                    <StyleTd>(Integer) - The Meursing Code</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>EA</b>
                    </StyleTd>
                    <StyleTd></StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>ADSZ</b>
                    </StyleTd>
                    <StyleTd></StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>ExtraDutyUSA</b> (Decimal)
                    </StyleTd>
                    <StyleTd>An additional duty imposed for USA imports</StyleTd>
                  </tr>
                  {/* -- Options -- */}
                  <tr>
                    <StyleTd>
                      <b>DeletePreviousInfoEntirely</b> (yes/no)
                    </StyleTd>
                    <StyleTd>This will wipe the entire data and start over</StyleTd>
                  </tr>
                  <tr>
                    <StyleTd>
                      <b>CreateIfMissing</b> (yes/no)
                    </StyleTd>
                    <StyleTd>This will create the supplier for this product if it does not exist</StyleTd>
                  </tr>
                </tbody>
              </Table>
              <Formik
                initialValues={{
                  xlsx: [],
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => onSubmitMine(values, setSubmitting)}
              >
                {({ values, handleChange, handleBlur, isSubmitting, setFieldValue, onSubmit, handleSubmit }) => (
                  <form autoComplete="off" onSubmit={handleSubmit} style={{ marginTop: '30px' }}>
                    <FormRow>
                      <FileInputComponent
                        name="xlsx"
                        value={values.xlsx}
                        onBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        cols={12}
                      />
                    </FormRow>
                    <div style={{ marginTop: '40px' }}>
                      <SecondaryButton
                        icon={uploadIcon}
                        type="submit"
                        isSubmitting={isSubmitting}
                        title="Upload"
                      />
                    </div>
                  </form>
                )}
              </Formik>
            </div>
          </FormWrapper>
          <div>{status}</div>
        </div>
      </div>
    </WhitePad>
  );
}

const Table = styled.div`
  border: 1px solid #fff;
  border-radius: 10px;
  overflow: hidden;
`;

const StyleTd = styled.td`
  width: 300px;
  border: 1px solid #fff;
  padding: 7px;
  font-size: 14px;
`;

const StyleP = styled.p`
  font-size: 14px;
  line-height: 17pxl
  color: #343434;
  text-align: center;
  margin-bottom: 43px;
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 20px;
  line-height: 23px;
  text-align: center;
  text-transform: uppercase;
  color: ${Colors.darkBrown};
`;

const ActionWrapper = styled.div`
  div {
    display: flex;
    justify-content: center;
  }
`;
